import {
  Box,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { useState } from 'react';

import Button from 'components/Button';
import Text from 'components/Text';

import { useEditRoastMutation } from '../../../hooks/mutations/roast/useEditRoastMutation';
import {
  CATEGORY_DISPLAY_LABELS,
  PRICING_DISPLAY_LABELS,
  PRICING_MODELS,
  SITE_CATEGORIES,
  type RoastData,
} from '../utils';

interface RoastMetadataEditorProps {
  roast: RoastData;
  isOpen: boolean;
  onClose: () => void;
}

const TAB_STYLE_ACTIVE = {
  borderBottom: '2px solid',
  borderColor: 'orange.500',
  color: 'white',
};

const TAB_STYLE_INACTIVE = {
  borderBottom: '2px solid',
  borderColor: 'transparent',
  color: 'whiteAlpha.500',
};

const RoastMetadataEditor = ({ roast, isOpen, onClose }: RoastMetadataEditorProps) => {
  const [tab, setTab] = useState<'general' | 'contact'>('general');
  const [siteName, setSiteName] = useState(roast.site_name ?? '');
  const [siteDescription, setSiteDescription] = useState(roast.site_description ?? '');
  const [siteLogoUrl, setSiteLogoUrl] = useState(roast.site_logo_url ?? '');
  const [category, setCategory] = useState(roast.category ?? 'other');
  const [pricingModel, setPricingModel] = useState(roast.pricing_model ?? 'unknown');
  const [tagsInput, setTagsInput] = useState((roast.tags ?? []).join(', '));
  const [socialsX, setSocialsX] = useState(roast.socials?.x ?? '');
  const [socialsGithub, setSocialsGithub] = useState(roast.socials?.github ?? '');
  const [socialsInstagram, setSocialsInstagram] = useState(roast.socials?.instagram ?? '');
  const [socialsLinkedin, setSocialsLinkedin] = useState(roast.socials?.linkedin ?? '');
  const [socialsTiktok, setSocialsTiktok] = useState(roast.socials?.tiktok ?? '');
  const [socialsReddit, setSocialsReddit] = useState(roast.socials?.reddit ?? '');
  const [contactEmail, setContactEmail] = useState(roast.contact_email ?? '');

  const { mutate: editRoast, isPending } = useEditRoastMutation();

  const handleSave = () => {
    const tags = tagsInput
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    const socials: Record<string, string> = {};
    if (socialsX.trim()) socials.x = socialsX.trim();
    if (socialsGithub.trim()) socials.github = socialsGithub.trim();
    if (socialsInstagram.trim()) socials.instagram = socialsInstagram.trim();
    if (socialsLinkedin.trim()) socials.linkedin = socialsLinkedin.trim();
    if (socialsTiktok.trim()) socials.tiktok = socialsTiktok.trim();
    if (socialsReddit.trim()) socials.reddit = socialsReddit.trim();

    editRoast(
      {
        roastId: roast.id,
        site_name: siteName,
        site_description: siteDescription,
        site_logo_url: siteLogoUrl,
        category,
        pricing_model: pricingModel,
        tags,
        socials,
        contact_email: contactEmail,
      },
      { onSuccess: () => onClose() }
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent backgroundColor="gray.800" color="white">
        <ModalHeader>Edit Roast Metadata</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {/* Tabs */}
          <HStack spacing={0} borderBottom="1px solid" borderColor="whiteAlpha.100" mb={4}>
            <Box
              as="button"
              px={4}
              py={2.5}
              fontSize="13px"
              fontWeight="medium"
              cursor="pointer"
              transition="all 0.15s"
              _hover={{ color: 'white' }}
              {...(tab === 'general' ? TAB_STYLE_ACTIVE : TAB_STYLE_INACTIVE)}
              onClick={() => setTab('general')}
            >
              General
            </Box>
            <Box
              as="button"
              px={4}
              py={2.5}
              fontSize="13px"
              fontWeight="medium"
              cursor="pointer"
              transition="all 0.15s"
              _hover={{ color: 'white' }}
              {...(tab === 'contact' ? TAB_STYLE_ACTIVE : TAB_STYLE_INACTIVE)}
              onClick={() => setTab('contact')}
            >
              Contact & Socials
            </Box>
          </HStack>

          {/* Tab: General */}
          {tab === 'general' && (
            <VStack spacing={4} alignItems="flex-start">
              {/* Read-only URL */}
              <Box width="100%">
                <Text fontSize="small" fontWeight="semibold" mb={1}>
                  Project URL
                </Text>
                <Input
                  value={roast.url}
                  isReadOnly
                  size="sm"
                  opacity={0.5}
                  cursor="default"
                />
                <Text fontSize="xMicro" opacity="tertiary" mt={1}>
                  Want to change the URL? Contact{' '}
                  <Box as="a" href="mailto:adrian@bigdevsoon.me" color="purple.300" _hover={{ color: 'purple.200' }}>
                    adrian@bigdevsoon.me
                  </Box>
                </Text>
              </Box>

              <Box width="100%">
                <Text fontSize="small" fontWeight="semibold" mb={1}>
                  Site Name
                </Text>
                <Input
                  value={siteName}
                  onChange={(e) => setSiteName(e.target.value)}
                  maxLength={100}
                  placeholder="My Project"
                  size="sm"
                />
              </Box>

              <Box width="100%">
                <Text fontSize="small" fontWeight="semibold" mb={1}>
                  Description
                </Text>
                <Textarea
                  value={siteDescription}
                  onChange={(e) => setSiteDescription(e.target.value)}
                  maxLength={500}
                  placeholder="A brief description of your project"
                  size="sm"
                  rows={3}
                />
              </Box>

              <Box width="100%">
                <Text fontSize="small" fontWeight="semibold" mb={1}>
                  Logo URL
                </Text>
                <Input
                  value={siteLogoUrl}
                  onChange={(e) => setSiteLogoUrl(e.target.value)}
                  maxLength={2048}
                  placeholder="https://example.com/logo.png"
                  size="sm"
                />
                <Text fontSize="xMicro" opacity="tertiary" mt={1}>
                  Paste a direct image URL (favicon or logo). Recommended: square, at least 128×128px.
                </Text>
              </Box>

              <HStack width="100%" spacing={4}>
                <Box flex={1}>
                  <Text fontSize="small" fontWeight="semibold" mb={1}>
                    Category
                  </Text>
                  <Select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    size="sm"
                  >
                    {SITE_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>
                        {CATEGORY_DISPLAY_LABELS[cat] ?? cat}
                      </option>
                    ))}
                  </Select>
                </Box>
                <Box flex={1}>
                  <Text fontSize="small" fontWeight="semibold" mb={1}>
                    Pricing
                  </Text>
                  <Select
                    value={pricingModel}
                    onChange={(e) => setPricingModel(e.target.value)}
                    size="sm"
                  >
                    {PRICING_MODELS.map((pm) => (
                      <option key={pm} value={pm}>
                        {PRICING_DISPLAY_LABELS[pm] ?? pm}
                      </option>
                    ))}
                  </Select>
                </Box>
              </HStack>

              <Box width="100%">
                <Text fontSize="small" fontWeight="semibold" mb={1}>
                  Tags (comma-separated)
                </Text>
                <Input
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  placeholder="e.g. developer-tool, cdn, open-source"
                  size="sm"
                />
              </Box>
            </VStack>
          )}

          {/* Tab: Contact & Socials */}
          {tab === 'contact' && (
            <VStack spacing={4} alignItems="flex-start">
              <Box width="100%">
                <Text fontSize="small" fontWeight="semibold" mb={1}>
                  Contact Email
                </Text>
                <Input
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  maxLength={254}
                  placeholder="you@example.com"
                  size="sm"
                  type="email"
                />
                <Text fontSize="xMicro" opacity="tertiary" mt={1}>
                  Optional — shown publicly so people can reach you
                </Text>
              </Box>

              <Box width="100%">
                <Text fontSize="small" fontWeight="semibold" mb={2}>
                  Social Handles
                </Text>
                <Text fontSize="xMicro" opacity="tertiary" mb={3}>
                  Enter handles only (e.g. @myproject). Links are built automatically.
                </Text>
                <VStack spacing={2}>
                  <Input
                    value={socialsX}
                    onChange={(e) => setSocialsX(e.target.value)}
                    maxLength={200}
                    placeholder="X / Twitter (e.g. @myproject)"
                    size="sm"
                  />
                  <Input
                    value={socialsGithub}
                    onChange={(e) => setSocialsGithub(e.target.value)}
                    maxLength={200}
                    placeholder="GitHub (e.g. myproject)"
                    size="sm"
                  />
                  <Input
                    value={socialsLinkedin}
                    onChange={(e) => setSocialsLinkedin(e.target.value)}
                    maxLength={200}
                    placeholder="LinkedIn (e.g. myproject)"
                    size="sm"
                  />
                  <Input
                    value={socialsInstagram}
                    onChange={(e) => setSocialsInstagram(e.target.value)}
                    maxLength={200}
                    placeholder="Instagram (e.g. @myproject)"
                    size="sm"
                  />
                  <Input
                    value={socialsTiktok}
                    onChange={(e) => setSocialsTiktok(e.target.value)}
                    maxLength={200}
                    placeholder="TikTok (e.g. @myproject)"
                    size="sm"
                  />
                  <Input
                    value={socialsReddit}
                    onChange={(e) => setSocialsReddit(e.target.value)}
                    maxLength={200}
                    placeholder="Reddit (e.g. r/myproject)"
                    size="sm"
                  />
                </VStack>
              </Box>
            </VStack>
          )}

          <HStack width="100%" justifyContent="flex-end" spacing={2} pt={4}>
            <Button scheme="ghost" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button scheme="primary" size="sm" onClick={handleSave} isLoading={isPending}>
              Save Changes
            </Button>
          </HStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default RoastMetadataEditor;
